// Kachel „Werke“ im Raster der Space-Seite (Welle 5, V5.4, Korrekturrunde 1: Material UI).
// MUI Card mit CardActionArea. Die Klasse iio-space-card liefert Fläche, Rahmen, Höhe und Hover
// genau wie bei den Instanzkarten derselben Seite; das Innenmaß trägt die CardActionArea, damit
// die ganze Fläche klickbar ist. Sichtbar für alle Leser; ob bearbeitet werden darf, sagt der Server
// (editable). Ein Ladefehler blendet die Kachel nicht aus, sonst wären die Werke unauffindbar.
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, CardActionArea, Typography } from "@mui/material";
import { api } from "../utils/helpers";
import SpaceWerkeDialog from "./SpaceWerkeDialog";
import { differingCompare, toRows } from "./spaceWerkeModel";

// Gleiche Strichstärke und Größe wie die InstanceIcon-Symbole der Nachbarkarten.
const PlantIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 20h20"/><path d="M4 20V9l5 3V9l5 3V5h4l1 15"/><path d="M8 16h1"/><path d="M12 16h1"/><path d="M16 16h1"/>
  </svg>
);

// 16 px wie .iio-card-stats--divided .iio-stat-val der Instanzkarten.
const statValSx = { fontFamily: "var(--iio-font-mono)", fontSize: 16, fontWeight: 600, lineHeight: 1, color: "var(--iio-text-1)", fontVariantNumeric: "tabular-nums" };
const statLblSx = { fontFamily: "var(--iio-font-mono)", fontSize: 9, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--iio-text-3)" };

function Kennzahl({ id, wert, label }) {
  return (
    <Box component="span" className="iio-card-stat" data-kennzahl={id}>
      <Typography component="span" sx={statValSx}>{wert}</Typography>
      <Typography component="span" sx={statLblSx}>{label}</Typography>
    </Box>
  );
}

export default function SpaceWerkeTile({ space, instances, animationDelay }) {
  const { t, i18n } = useTranslation(["space", "gemeinsam"]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const spaceId = space?.id;
  const aktuell = useRef(0);
  const titelId = `iio-werke-kachel-${useId().replace(/:/g, "")}`;
  const beschreibungId = `${titelId}-beschreibung`;

  // Liefert die frischen Daten oder null bei einem Fehler. Ein Fehler lässt vorhandene Daten
  // stehen, damit der Dialog nach einem gescheiterten Nachladen nicht auf einen alten Stand fällt.
  const load = useCallback(async () => {
    if (!spaceId) return null;
    const lauf = ++aktuell.current;
    setLoading(true);
    try {
      const d = await api(`/api/spaces/${spaceId}/werke`);
      if (lauf !== aktuell.current) return null;
      const sauber = d && typeof d === "object" ? d : null;
      setData(sauber);
      setLoadError(null);
      return sauber;
    } catch (e) {
      if (lauf === aktuell.current) setLoadError(e?.message || "error");
      return null;
    } finally {
      if (lauf === aktuell.current) setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { aktuell.current += 1; }, []);

  // Antwort des PUT direkt übernehmen (V5.1: { werke, revision, source: "space" }).
  const gespeichert = useCallback(res => {
    if (!res || typeof res !== "object") return;
    setData(prev => ({ ...(prev || {}), werke: res.werke, revision: res.revision ?? null, source: res.source || "space" }));
  }, []);

  const oeffnen = () => { setOpen(true); load(); };
  const anzahl = toRows(data?.werke).length;
  const ausInstanz = data?.source === "instance";
  const abweichend = ausInstanz ? differingCompare(data?.compare).length : 0;
  const quelle = data?.sourceInstanceId;
  const sourceName = quelle
    ? (data?.sourceInstanceName
      || (instances || []).find(i => i.id === quelle)?.name
      || (Array.isArray(data?.compare) ? data.compare.find(c => c?.instanceId === quelle)?.instanceName : null)
      || null)
    : null;
  const zahl = x => (x ?? 0).toLocaleString(i18n.language === "en" ? "en-GB" : "de-DE");

  const beschreibung = !data
    ? (loadError ? t("spaceWerke.kachel.ladeFehler") : null)
    : ausInstanz
      ? (sourceName ? t("spaceWerke.kachel.ausInstanzHinweis", { name: sourceName }) : t("spaceWerke.kachel.ausInstanzHinweisOhneName"))
      : anzahl ? t("spaceWerke.kachel.amSpace") : t("spaceWerke.kachel.leer");

  return (
    <>
      <Card component="article" elevation={0} className="iio-space-card iio-anim-stagger" data-kachel="werke"
        // Innenabstand wandert in die CardActionArea, damit die ganze Kachel klickbar ist. Inline,
        // weil die Klassenregel .iio .iio-space-card sonst stärker wäre als sx.
        style={{ padding: 0, ...(animationDelay ? { animationDelay } : {}) }}>
        <CardActionArea onClick={oeffnen} disableRipple aria-labelledby={titelId} aria-describedby={beschreibungId}
          sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start",
            // font: inherit, sonst setzt der button die UA-Schrift statt Source Sans 3 der Seite.
            p: 3, borderRadius: "inherit", color: "inherit", font: "inherit", fontFamily: "inherit", textAlign: "left",
            "& .MuiCardActionArea-focusHighlight": { display: "none" },
            "&.Mui-focusVisible": { outline: "2px solid var(--iio-brand-fg)", outlineOffset: -2 } }}>
          <Box component="span" className="iio-card-head">
            <Box component="span" className="iio-card-icon"><PlantIcon/></Box>
            <Box component="span" className="iio-card-title">
              {/* Innerhalb der CardActionArea (ein button) ist nur Inhalt auf Zeilenebene gültig, daher
                  span statt h3. Name und Beschreibung des Knopfs kommen über aria-labelledby/-describedby. */}
              <Typography component="span" id={titelId} sx={{ display: "block", m: "0 0 6px", fontFamily: "inherit", fontSize: 17, fontWeight: 600, lineHeight: 1.2,
                letterSpacing: "-.01em", color: "var(--iio-text-1)" }}>
                {t("spaceWerke.kachel.titel")}
              </Typography>
              {data && ausInstanz && (
                <Typography component="span" sx={{ display: "block", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: "var(--iio-warning)" }}>
                  {t("spaceWerke.kachel.ausInstanz")}
                </Typography>
              )}
            </Box>
          </Box>

          <Typography component="span" id={beschreibungId} className={`iio-card-desc${!beschreibung ? " iio-card-desc--empty" : ""}`}
            sx={{ fontFamily: "inherit", fontSize: 13, lineHeight: 1.55 }}>
            {beschreibung || "…"}
          </Typography>

          <Box component="span" className="iio-card-foot">
            <Box component="span" className="iio-card-stats iio-card-stats--divided">
              <Kennzahl id="anzahl" wert={data ? zahl(anzahl) : (loading ? "…" : "-")} label={t("spaceWerke.kachel.anzahl")}/>
              {data && ausInstanz && <Kennzahl id="abweichungen" wert={zahl(abweichend)} label={t("spaceWerke.kachel.abweichungen")}/>}
            </Box>
          </Box>
        </CardActionArea>
      </Card>

      {open && (
        <SpaceWerkeDialog
          space={space}
          data={data}
          loading={loading}
          loadError={loadError}
          sourceName={sourceName}
          onReload={load}
          onSaved={gespeichert}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
